import React from 'react';
import {
  CheckCircle, Clock, Edit3, Eye, Archive, AlertTriangle,
  Send, FileText, RotateCcw, Sparkles,
} from 'lucide-react';

export type QAStatus = 'draft' | 'in_progress' | 'in_review' | 'updated' | 'approved' | 'live' | 'archived';
export type TranslationStatusType = 'synced' | 'auto_translated' | 'manually_reviewed' | 'manually_edited' | 'outdated' | 'pending_approval' | 'translation_error';

const STATUS_CONFIG: Record<QAStatus, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  draft: { label: 'Entwurf', color: '#6B7280', bg: 'rgba(107,114,128,0.12)', icon: <FileText size={12} /> },
  in_progress: { label: 'In Bearbeitung', color: '#3B82F6', bg: 'rgba(59,130,246,0.12)', icon: <Edit3 size={12} /> },
  in_review: { label: 'In Prüfung', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', icon: <Eye size={12} /> },
  updated: { label: 'Aktualisiert', color: '#F97316', bg: 'rgba(249,115,22,0.12)', icon: <RotateCcw size={12} /> },
  approved: { label: 'Freigegeben', color: '#22C55E', bg: 'rgba(34,197,94,0.12)', icon: <CheckCircle size={12} /> },
  live: { label: 'Live', color: '#059669', bg: 'rgba(5,150,105,0.12)', icon: <Send size={12} /> },
  archived: { label: 'Archiviert', color: '#9CA3AF', bg: 'rgba(156,163,175,0.12)', icon: <Archive size={12} /> },
};

const TRANS_STATUS_CONFIG: Record<TranslationStatusType, { label: string; color: string; bg: string }> = {
  synced: { label: 'Synchron', color: '#22C55E', bg: 'rgba(34,197,94,0.15)' },
  auto_translated: { label: 'Auto-Übersetzt', color: '#3B82F6', bg: 'rgba(59,130,246,0.15)' },
  manually_reviewed: { label: 'Manuell geprüft', color: '#059669', bg: 'rgba(5,150,105,0.15)' },
  manually_edited: { label: 'Manuell bearbeitet', color: '#8B5CF6', bg: 'rgba(139,92,246,0.15)' },
  outdated: { label: 'Veraltet', color: '#EF4444', bg: 'rgba(239,68,68,0.15)' },
  pending_approval: { label: 'Freigabe ausstehend', color: '#F59E0B', bg: 'rgba(245,158,11,0.15)' },
  translation_error: { label: 'Fehler', color: '#EF4444', bg: 'rgba(239,68,68,0.2)' },
};

export const QAStatusBadge: React.FC<{ status: QAStatus; size?: 'sm' | 'md' }> = ({ status, size = 'sm' }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const fontSize = size === 'sm' ? 10 : 12;
  const padding = size === 'sm' ? '2px 8px' : '4px 12px';

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize, fontWeight: 700, padding, borderRadius: 6,
      color: config.color, background: config.bg,
      whiteSpace: 'nowrap', lineHeight: 1.4,
    }}>
      {config.icon} {config.label}
    </span>
  );
};

export const TranslationBadge: React.FC<{ status: TranslationStatusType; size?: 'sm' | 'md' }> = ({ status, size = 'sm' }) => {
  const config = TRANS_STATUS_CONFIG[status] || TRANS_STATUS_CONFIG.synced;
  const fontSize = size === 'sm' ? 9 : 11;

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
      color: config.color, background: config.bg,
      whiteSpace: 'nowrap',
    }}>
      {config.label}
    </span>
  );
};

export const TranslationDot: React.FC<{ status: TranslationStatusType }> = ({ status }) => {
  const config = TRANS_STATUS_CONFIG[status];
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: config?.color || '#6B7280',
      boxShadow: status === 'translation_error' ? `0 0 4px ${config?.color}` : undefined,
    }} title={config?.label || status} />
  );
};

export const ChangeMarkerBadge: React.FC<{ marker: string }> = ({ marker }) => {
  const MARKER_MAP: Record<string, { emoji: string; label: string }> = {
    text_changed: { emoji: '📝', label: 'Text' },
    options_changed: { emoji: '🔄', label: 'Antworten' },
    explanation_changed: { emoji: '💡', label: 'Erklärung' },
    correct_changed: { emoji: '✅', label: 'Korrekte Antwort' },
    image_changed: { emoji: '🖼️', label: 'Bild' },
    rollback: { emoji: '⏪', label: 'Rollback' },
  };

  const config = MARKER_MAP[marker] || { emoji: '•', label: marker };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 2,
      fontSize: 9, padding: '1px 5px', borderRadius: 4,
      background: 'rgba(59,130,246,0.1)', color: 'var(--text-muted)',
    }}>
      {config.emoji} {config.label}
    </span>
  );
};

export const CHANGE_TYPE_ICONS: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  edit: { icon: <Edit3 size={12} />, color: '#3B82F6', label: 'Bearbeitet' },
  approve: { icon: <CheckCircle size={12} />, color: '#22C55E', label: 'Freigegeben' },
  publish: { icon: <Send size={12} />, color: '#059669', label: 'Live geschaltet' },
  rollback: { icon: <RotateCcw size={12} />, color: '#F59E0B', label: 'Rollback' },
  auto_translate: { icon: <Sparkles size={12} />, color: '#8B5CF6', label: 'Auto-Übersetzt' },
  initial: { icon: <FileText size={12} />, color: '#6B7280', label: 'Erstellt' },
};
